/**
 * The turn clock.
 *
 * The server sends `remainingMs` **once per turn**, not every tick — ticking it
 * server-side would rebroadcast to the whole room several times a second to
 * animate a number. So the countdown runs locally from the moment that value
 * arrived, and any drift is corrected by the next turn's message.
 *
 * The consequence worth knowing: this clock is an *indicator*, never an
 * authority. The server decides when a turn has actually expired, and it uses
 * its own tick count. A client whose tab was throttled may show a second or two
 * of slack; it cannot cause or prevent an auto-play.
 */

import { useEffect, useRef, useState } from 'react';
import type { TurnInfo } from '../net/match.js';
import { isWarning, remainingFrom, secondsLeft, sweepFraction, tickDue } from './countdown.js';
import { playTick } from './sound.js';

/**
 * Milliseconds left, counted down locally.
 *
 * Re-anchors whenever the server sends a new figure. `Date.now` is the right
 * clock here despite being wall time: a backgrounded tab stops firing timers,
 * and an interval-counted clock would come back wrong by however long the phone
 * was asleep, while a timestamp difference comes back correct.
 */
function useCountdown(turn: TurnInfo | null): number | null {
  const [now, setNow] = useState(() => Date.now());
  /*
   * The anchor is STATE, not a ref.
   *
   * Held in a ref, the effect that set it had to force a repaint with
   * `setNow(Date.now())` - and when that timestamp matched the current one to
   * the millisecond, React bailed out of the render and the clock never
   * appeared at all. It is a value the render reads, so it belongs in state.
   */
  const [anchor, setAnchor] = useState<{ at: number; remainingMs: number } | null>(null);

  const remaining = turn?.remainingMs ?? null;
  const seat = turn?.seat ?? null;

  useEffect(() => {
    if (remaining === null) {
      setAnchor(null);
      return;
    }
    const at = Date.now();
    setAnchor({ at, remainingMs: remaining });
    setNow(at);
  }, [remaining, seat]);

  useEffect(() => {
    if (remaining === null) return undefined;
    // Four times a second: smooth enough that the last few seconds do not
    // visibly stutter, cheap enough to ignore.
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, [remaining]);

  return remainingFrom(anchor, now);
}

/** Is this turn inside its warning window? */
export function useTurnWarning(turn: TurnInfo | null): boolean {
  const left = useCountdown(turn);
  if (!turn) return false;
  return isWarning(left, turn.warningMs);
}

/**
 * The avatar, with the turn clock drawn around it (H11).
 *
 * The countdown used to be a separate dial beside the seat. On the table it is
 * now the ring of whoever's turn it is, because "whose turn is it" and "how
 * long do they have" are the same question and deserve one answer in one place.
 * It also costs no screen space, which matters on a phone that already stacks a
 * hand, an avatar, an action row and a chat bar.
 *
 * This could not simply be dropped the way the mockups drop it: a seat that
 * runs out is auto-played, so a hidden countdown means a turn taken with no
 * warning — the game playing itself while you watch. It works for opponents for
 * the same reason, so you can see somebody about to time out.
 *
 * ## The number is deliberately still here
 *
 * A pure sweep has no text, and a screen reader would lose the countdown
 * entirely. So the seconds stay: `role="timer"` carries them for assistive
 * tech, and a small badge keeps them visible, because the sighted reason for
 * wanting them — a seat is about to be played for — has not gone away either.
 *
 * The geometry is in viewBox units rather than pixels so one component serves
 * every seat size; the avatar is sized in CSS and the ring follows it.
 */
export function SeatAvatarRing({
  turn,
  children,
}: {
  turn: TurnInfo | null;
  children: React.ReactNode;
}) {
  const left = useCountdown(turn);
  const ticking = left !== null && turn !== null;

  const seconds = ticking ? secondsLeft(left) : 0;
  const warning = ticking && isWarning(left, turn.warningMs);
  const fraction = ticking ? sweepFraction(left, turn.limitMs) : 0;

  // r=46 in a 100-unit box leaves room for the stroke without clipping it.
  const circumference = 2 * Math.PI * 46;

  return (
    <span className={`avatar-ring${ticking ? ' ticking' : ''}${warning ? ' warning' : ''}`}>
      {/*
        One circle doing two jobs.

        H6 frames every avatar in gold and H11 sweeps a clock around the active
        one. Drawn separately those would be two rings on one face, so the frame
        IS the clock's track: it is always there, and on the seat being waited
        on the sweep runs over it.
      */}
      <svg className="avatar-sweep" viewBox="0 0 100 100" aria-hidden="true">
        <circle className="clock-track" cx="50" cy="50" r="46" fill="none" />
        {ticking ? (
          <circle
            className="clock-sweep"
            cx="50"
            cy="50"
            r="46"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - fraction)}
            // Starts the sweep at twelve o'clock rather than three.
            transform="rotate(-90 50 50)"
          />
        ) : null}
      </svg>

      <span className="avatar" aria-hidden="true">
        {children}
      </span>

      {ticking ? (
        <span className="avatar-seconds" role="timer" aria-label={`${String(seconds)} seconds left`}>
          {seconds}
        </span>
      ) : null}
    </span>
  );
}

/**
 * One tick a second, for the last ten.
 *
 * Keyed on the WHOLE second rather than on `left`, which changes four times a
 * second - so the sound fires when the number a player is reading changes, and
 * exactly once each time it does.
 *
 * The ref is what makes it once. An effect that simply played on every run
 * inside the window would tick four times a second; remembering which second
 * has already been spoken is the whole mechanism.
 *
 * Muting is not checked here on purpose: `playTick` goes through the same
 * `audio()` gate as every other sound, which hands back nothing at all while
 * the player has sound off. One place decides that.
 */
function useTicking(left: number | null, enabled: boolean): void {
  const spoken = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled || left === null) {
      spoken.current = null;
      return;
    }

    const seconds = secondsLeft(left);
    const due = tickDue(spoken.current, seconds);

    // Remembered even when no tick was due, so re-entering the window - a new
    // turn - starts from a clean number rather than repeating the last one
    // heard.
    spoken.current = seconds;
    if (due) playTick();
  }, [left, enabled]);
}

export function Clock({
  turn,
  big,
  audible,
}: {
  turn: TurnInfo | null;
  big?: boolean | undefined;
  /**
   * Count the last ten seconds out loud.
   *
   * Off by default and opted into at the one call site that is the player's
   * OWN turn. A table where every seat's clock ticked on every phone in the
   * room would be four people hearing a countdown for a turn three of them
   * cannot act on.
   */
  audible?: boolean | undefined;
}) {
  const left = useCountdown(turn);

  // Above the early return, and it has to be: a hook below one runs a
  // different number of times depending on the branch, which is the crash
  // HostControls shipped with.
  useTicking(left, audible === true);

  if (left === null || !turn) return null;

  const seconds = secondsLeft(left);
  const warning = isWarning(left, turn.warningMs);
  const fraction = sweepFraction(left, turn.limitMs);
  const size = big ? 64 : 38;
  const radius = size / 2 - 4;
  const circumference = 2 * Math.PI * radius;

  return (
    <span
      className={`clock${warning ? ' warning' : ''}${big ? ' big' : ''}`}
      role="timer"
      aria-label={`${String(seconds)} seconds left`}
    >
      <svg width={size} height={size} viewBox={`0 0 ${String(size)} ${String(size)}`}>
        <circle className="clock-track" cx={size / 2} cy={size / 2} r={radius} fill="none" />
        <circle
          className="clock-sweep"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - fraction)}
          // Starts the sweep at twelve o'clock rather than three.
          transform={`rotate(-90 ${String(size / 2)} ${String(size / 2)})`}
        />
      </svg>
      <span className="clock-number">{seconds}</span>
    </span>
  );
}
